/**
 * @file Helpers for interpreting TokenEx iframe error payloads.
 *
 * The TokenEx iframe sends its `error` event as an object whose message sits on `error`,
 * e.g. `{ error: "Invalid Authentication Key - Session expired" }`. Formatting that object
 * into a template string gets the literal text "[object Object]".
 *
 */

const MAX_SERIALIZED_LENGTH = 200;

/**
 * Reduces any TokenEx error payload (event object, Error, or string) to a readable message.
 */
export function toTokenExErrorMessage(payload: unknown): string {
  if (!payload) {
    return "Unknown TokenEx error";
  }

  if (typeof payload === "string") {
    return payload.slice(0, MAX_SERIALIZED_LENGTH);
  }

  const candidate = payload as { error?: unknown; errorMessage?: unknown; message?: unknown };
  const message = candidate.error ?? candidate.errorMessage ?? candidate.message;

  if (typeof message === "string" && message.length) {
    return message.slice(0, MAX_SERIALIZED_LENGTH);
  }

  try {
    return `TokenEx error, unrecognised shape {${Object.keys(payload as object).join(", ")}}`.slice(
      0,
      MAX_SERIALIZED_LENGTH
    );
  } catch {
    return "TokenEx error, unrecognised payload";
  }
}

/**
 * TokenEx authentication keys expire 20 minutes after they are issued.
 */
export function isTokenExExpiryError(payload: unknown): boolean {
  const message = toTokenExErrorMessage(payload).toLowerCase();

  return message.includes("session expired") || message.includes("authentication key");
}
